import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowUpRight, Mail, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { FormattedMessage, useIntl } from "react-intl";

import { getMe, login } from "@/features/auth/auth.api";
import { useAuthStore } from "@/lib/auth-store";
import { ApiError } from "@/lib/api";

// ───────────────────────── ECG waveform path ─────────────────────────
// Un latido completo (P-QRS-T) cada 200px en un viewBox de 2000px de ancho.
// Se anima trasladando -50% para loop seamless (duplicamos el patrón).
const ECG_PERIOD = 200;
function buildECG(periods = 10, baseline = 60, width = ECG_PERIOD): string {
  let d = "";
  for (let i = 0; i < periods; i++) {
    const x = i * width;
    d += (i === 0 ? "M " : "L ") + x + " " + baseline + " ";
    d += `L ${x + 40} ${baseline} `;
    d += `Q ${x + 52} ${baseline - 8}, ${x + 64} ${baseline} `; // P wave
    d += `L ${x + 86} ${baseline} `;
    d += `L ${x + 92} ${baseline + 10} `; // Q
    d += `L ${x + 98} ${baseline - 42} `; // R (up spike)
    d += `L ${x + 104} ${baseline + 18} `; // S
    d += `L ${x + 110} ${baseline} `;
    d += `Q ${x + 130} ${baseline - 16}, ${x + 150} ${baseline} `; // T wave
    d += `L ${x + width} ${baseline} `;
  }
  return d;
}

const ECG_D = buildECG(10);

// ───────────────────────── Live metrics (mock vivo) ─────────────────────────
function useLiveMetrics() {
  const [metrics, setMetrics] = useState({
    active: 1247,
    reasoners: 14,
    casesToday: 89,
    traces: 2184,
    latency: 312,
  });
  useEffect(() => {
    const id = setInterval(() => {
      setMetrics((m) => ({
        active: m.active + Math.floor(Math.random() * 5) - 2,
        reasoners: Math.max(4, m.reasoners + (Math.random() > 0.5 ? 1 : -1)),
        casesToday: m.casesToday + (Math.random() > 0.7 ? 1 : 0),
        traces: m.traces + Math.floor(Math.random() * 3),
        latency: Math.max(180, Math.floor(m.latency + (Math.random() * 30 - 15))),
      }));
    }, 2400);
    return () => clearInterval(id);
  }, []);
  return metrics;
}

// ───────────────────────── Clock ─────────────────────────
function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function fmtTime(d: Date, locale: string) {
  return d.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}
function fmtDate(d: Date, locale: string) {
  return d.toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "short",
  });
}

// ───────────────────────── Page ─────────────────────────
export function LoginPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const setUser = useAuthStore((s) => s.setUser);
  const isDev = import.meta.env.DEV;
  const [email, setEmail] = useState(isDev ? "alumno1@universidad.cl" : "");
  const [password, setPassword] = useState(isDev ? "simumed2026" : "");
  const [submitting, setSubmitting] = useState(false);
  const [emailFocus, setEmailFocus] = useState(false);
  const [pwFocus, setPwFocus] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const emailRef = useRef<HTMLInputElement>(null);
  const metrics = useLiveMetrics();
  const now = useClock();

  const t = (id: string) => intl.formatMessage({ id });

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setErrorMsg(null);
    setSubmitting(true);
    try {
      const tokens = await login(email, password, needsTotp ? totpCode : undefined);
      setSession({
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
      });
      const me = await getMe();
      setUser(me);
      toast.success(`${intl.formatMessage({ id: "common.welcome" })}, ${me.full_name}`);
      navigate("/app");
    } catch (err) {
      if (isTotpChallenge(err)) {
        setNeedsTotp(true);
        setErrorMsg(needsTotp ? t("login.form.totp.invalid") : null);
        return;
      }
      const msg = resolveErrorMessage(err);
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  function isTotpChallenge(err: unknown): boolean {
    if (!(err instanceof ApiError)) return false;
    if (err.status !== 401) return false;
    const d = err.details as { detail?: unknown } | null;
    const detail = d && typeof d === "object" ? d.detail : null;
    return (
      typeof detail === "object" &&
      detail !== null &&
      (detail as { requires_2fa?: boolean }).requires_2fa === true
    );
  }

  function resolveErrorMessage(err: unknown): string {
    if (!(err instanceof ApiError)) return t("login.error.network");
    switch (err.status) {
      case 400:
      case 422:
        return t("login.error.400");
      case 401:
        return t("login.error.401");
      case 403:
        return t("login.error.403");
      case 429:
        return err.message || t("login.error.429");
      case 500:
      case 502:
      case 503:
      case 504:
        return t("login.error.5xx");
      default:
        return err.message || t("login.error.default");
    }
  }

  const tickerItems = [
    { label: t("login.ticker.active"), value: metrics.active.toLocaleString(intl.locale) },
    { label: t("login.ticker.reasoners"), value: String(metrics.reasoners) },
    { label: t("login.ticker.casesToday"), value: String(metrics.casesToday) },
    { label: t("login.ticker.traces"), value: metrics.traces.toLocaleString(intl.locale) },
    { label: t("login.ticker.latency"), value: `${metrics.latency}ms` },
    { label: t("login.ticker.uptime"), value: "99.98%" },
  ];

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#05070f] text-white">
      {/* ═══════════════════════════════════════════════════════════
          BACKGROUND STACK
          ═══════════════════════════════════════════════════════════ */}

      {/* Deep radial wash */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(1200px 800px at 20% 0%, rgba(37,99,235,0.14), transparent 55%), radial-gradient(900px 700px at 100% 100%, rgba(212,168,67,0.08), transparent 60%)",
        }}
      />

      {/* Drifting aurora orb */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[20%] top-[10%] h-[720px] w-[720px] rounded-full aurora-drift"
        style={{
          background:
            "radial-gradient(circle, rgba(37,99,235,0.28) 0%, rgba(37,99,235,0) 65%)",
          filter: "blur(60px)",
        }}
      />

      {/* ECG line — vive en el tercio vertical central, ancho doble para loop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-[52%] -translate-y-1/2"
      >
        <svg
          className="ecg-flow h-[180px] w-[200%] opacity-[0.55]"
          viewBox={`0 0 ${ECG_PERIOD * 20} 120`}
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="ecg-stroke" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0" />
              <stop offset="8%" stopColor="#60a5fa" stopOpacity="0.9" />
              <stop offset="92%" stopColor="#60a5fa" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#60a5fa" stopOpacity="0" />
            </linearGradient>
            <filter id="ecg-glow" x="-20%" y="-50%" width="140%" height="200%">
              <feGaussianBlur stdDeviation="1.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {/* dos copias — una seguida de otra — para loop perfecto */}
          <g filter="url(#ecg-glow)">
            <path d={ECG_D} stroke="url(#ecg-stroke)" strokeWidth="1.3" fill="none" />
            <path
              d={ECG_D}
              stroke="url(#ecg-stroke)"
              strokeWidth="1.3"
              fill="none"
              transform={`translate(${ECG_PERIOD * 10}, 0)`}
            />
          </g>
        </svg>
      </div>

      {/* Noise — da "peso" y calma el gradiente limpio de SaaS */}
      <div aria-hidden className="noise-layer pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay" />

      {/* Vertical seam suave entre editorial y form en lg+ */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-[62%] hidden w-px bg-gradient-to-b from-transparent via-white/10 to-transparent lg:block"
      />

      {/* ═══════════════════════════════════════════════════════════
          TOP STRIP
          ═══════════════════════════════════════════════════════════ */}
      <header className="relative z-10 flex flex-wrap items-center justify-between gap-4 px-6 pt-6 sm:px-10 sm:pt-8">
        <div className="flex items-center gap-3">
          <StethoscopeMark />
          <div className="flex flex-col leading-none">
            <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-[0.32em] text-white/55">
              drnewenia
            </span>
            <span className="mt-1 text-[10px] text-white/45">v0.1</span>
          </div>
        </div>

        <div className="hidden items-center gap-6 text-[11px] md:flex">
          <div className="flex items-center gap-2 font-[var(--font-mono)] text-white/50">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            <span className="uppercase tracking-widest">{t("login.location")}</span>
          </div>
          <div className="font-[var(--font-mono)] uppercase tracking-widest text-white/55">
            <span className="text-white/80">{fmtTime(now, intl.locale)}</span>
            <span className="mx-2 text-white/20">│</span>
            <span>{fmtDate(now, intl.locale)}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-[var(--font-mono)] text-[9px] uppercase tracking-widest text-white/50">
              <FormattedMessage id="login.brand.partnerLabel" />
            </div>
            <div className="text-[11px] font-semibold text-white/80">
              <FormattedMessage id="login.brand.partnerValue" />
            </div>
          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════
          MAIN — asymmetric editorial grid
          ═══════════════════════════════════════════════════════════ */}
      <main className="relative z-10 grid flex-1 grid-cols-1 gap-12 px-6 pt-14 pb-24 sm:px-10 lg:grid-cols-[minmax(0,1.55fr)_minmax(360px,1fr)] lg:gap-20 lg:px-16 lg:pt-20">
        {/* ────────────── LEFT · Editorial hero ────────────── */}
        <section className="flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3">
              <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-[0.32em] text-[#60a5fa]">
                § 01
              </span>
              <span className="h-px w-10 bg-[#60a5fa]/40" />
              <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-[0.28em] text-white/65">
                <FormattedMessage id="login.eyebrow.suffix" defaultMessage="Simulador clínico longitudinal" />
              </span>
            </div>

            <h1 className="mt-8 font-[var(--font-display)] text-[clamp(3.5rem,9vw,8.5rem)] font-extrabold leading-[0.85] tracking-[-0.04em] text-white">
              <FormattedMessage id="login.hero.prefix" />
              <span
                className="font-[var(--font-serif)] italic font-normal"
                style={{
                  // inline-block + padding: el glifo itálico final ('d') sobresale de
                  // su caja y, con background-clip:text, se recortaba. El padding extiende
                  // la caja de pintado para que la 'd' se vea completa.
                  display: "inline-block",
                  paddingRight: "0.14em",
                  background:
                    "linear-gradient(135deg, #bfdbfe 0%, #60a5fa 40%, #D4A843 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                <FormattedMessage id="login.hero.suffix" />
              </span>
              <span className="text-[#60a5fa]">.</span>
            </h1>

            <p className="mt-8 max-w-2xl text-balance text-[15px] leading-[1.7] text-white/75 sm:text-[17px]">
              <FormattedMessage
                id="login.tagline"
                values={{
                  italic: (chunks) => (
                    <span className="font-[var(--font-serif)] italic text-white/80">
                      {chunks}
                    </span>
                  ),
                }}
              />
            </p>
          </motion.div>

          {/* Specs tipo anuario médico */}
          <motion.div
            className="mt-14 grid max-w-3xl grid-cols-1 gap-x-10 gap-y-8 border-t border-white/[0.08] pt-10 sm:grid-cols-3"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
            }}
          >
            {[
              {
                n: "01",
                title: t("login.feature.reasoning.title"),
                body: t("login.feature.reasoning.body"),
              },
              {
                n: "02",
                title: t("login.feature.longitudinal.title"),
                body: t("login.feature.longitudinal.body"),
              },
              {
                n: "03",
                title: t("login.feature.culture.title"),
                body: t("login.feature.culture.body"),
              },
            ].map((s) => (
              <motion.div
                key={s.n}
                variants={{
                  hidden: { opacity: 0, y: 14 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                }}
              >
                <div className="font-[var(--font-mono)] text-[11px] uppercase tracking-widest text-[#60a5fa]/70">
                  {s.n} —
                </div>
                <div className="mt-2 font-[var(--font-display)] text-[17px] font-bold leading-tight text-white/90">
                  {s.title}
                </div>
                <div className="mt-2 text-[13px] leading-relaxed text-white/65">
                  {s.body}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </section>

        {/* ────────────── RIGHT · form ────────────── */}
        <section className="flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md"
          >
            <div className="flex items-center gap-3">
              <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-[0.32em] text-[#D4A843]">
                § 02
              </span>
              <span className="h-px w-10 bg-[#D4A843]/40" />
              <span className="font-[var(--font-mono)] text-[10px] uppercase tracking-[0.28em] text-white/65">
                <FormattedMessage id="login.form.eyebrow" defaultMessage="Acceso institucional">
                  {(msg) => <>{String(msg).replace(/^§ 02 · /, "")}</>}
                </FormattedMessage>
              </span>
            </div>

            <h2 className="mt-6 font-[var(--font-display)] text-[44px] font-extrabold leading-[0.95] tracking-[-0.03em] text-white">
              <FormattedMessage id="login.form.heading.prefix" />{" "}
              <span className="font-[var(--font-serif)] italic font-normal text-white/80">
                <FormattedMessage id="login.form.heading.suffix" />
              </span>
              .
            </h2>
            <p className="mt-3 text-[13px] leading-relaxed text-white/65">
              <FormattedMessage id="login.form.subtitle" />
            </p>

            {/* Live error banner con role=alert */}
            {errorMsg && (
              <div
                id="login-error"
                role="alert"
                aria-live="polite"
                className="mt-6 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.08] px-3 py-2 text-[12.5px] text-red-200"
              >
                <span aria-hidden className="mt-0.5 text-red-400">⚠</span>
                <span>{errorMsg}</span>
              </div>
            )}

            <form onSubmit={onSubmit} className="mt-10 space-y-7" noValidate>
              {/* Email — underline style */}
              <FieldUnderline
                icon={<Mail size={14} />}
                label={t("login.form.email")}
                focused={emailFocus}
                filled={!!email}
              >
                <input
                  ref={emailRef}
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocus(true)}
                  onBlur={() => setEmailFocus(false)}
                  placeholder={t("login.form.email.placeholder")}
                  aria-invalid={!!errorMsg}
                  aria-describedby={errorMsg ? "login-error" : undefined}
                  className="w-full bg-transparent pb-2 pt-1 font-[var(--font-mono)] text-[15px] text-white placeholder:text-white/55 focus:outline-none"
                />
              </FieldUnderline>

              {/* Password */}
              <FieldUnderline
                icon={<Lock size={14} />}
                label={t("login.form.password")}
                focused={pwFocus}
                filled={!!password}
              >
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPwFocus(true)}
                  onBlur={() => setPwFocus(false)}
                  aria-invalid={!!errorMsg}
                  aria-describedby={errorMsg ? "login-error" : undefined}
                  className="w-full bg-transparent pb-2 pt-1 font-[var(--font-mono)] text-[15px] tracking-[0.3em] text-white placeholder:text-white/55 focus:outline-none"
                />
              </FieldUnderline>

              {/* 2FA challenge — aparece solo si backend lo requirió */}
              {needsTotp && (
                <FieldUnderline
                  icon={<ShieldCheck size={14} />}
                  label={t("login.form.totp")}
                  focused={false}
                  filled={totpCode.length === 6}
                >
                  <input
                    id="totp-code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    autoComplete="one-time-code"
                    required
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                    className="w-full bg-transparent pb-2 pt-1 font-[var(--font-mono)] text-[18px] tracking-[0.5em] text-white placeholder:text-white/55 focus:outline-none"
                    placeholder="000000"
                    aria-describedby={errorMsg ? "login-error" : undefined}
                  />
                </FieldUnderline>
              )}

              {/* Submit button — pill dramatic */}
              <button
                type="submit"
                disabled={submitting || (needsTotp && totpCode.length !== 6)}
                className="group relative mt-2 flex h-14 w-full items-center justify-between overflow-hidden rounded-full border border-white/10 bg-gradient-to-r from-[#2563eb] via-[#1d4ed8] to-[#1e3a8a] px-6 text-[15px] font-semibold text-white transition-all hover:border-white/25 hover:shadow-[0_0_40px_-8px_rgba(37,99,235,0.6)] disabled:cursor-not-allowed disabled:opacity-70"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 translate-x-[-120%] bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-[120%]"
                />
                <span className="relative flex items-center gap-2">
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />{" "}
                      <FormattedMessage id="login.form.submitting" />
                    </>
                  ) : (
                    <FormattedMessage id="login.form.submit" />
                  )}
                </span>
                <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition-all group-hover:translate-x-1 group-hover:bg-white/20">
                  <ArrowUpRight size={16} />
                </span>
              </button>
            </form>

            {/* Legal + dev-only seed hint */}
            <div className="mt-10 flex flex-col gap-3 border-t border-white/[0.06] pt-5 text-[11px] text-white/50">
              {isDev && (
                <div className="flex items-center justify-between font-[var(--font-mono)] uppercase tracking-widest">
                  <span>
                    <FormattedMessage id="login.account.testLabel" />
                  </span>
                  <span className="text-white/55">alumno1@universidad.cl · simumed2026</span>
                </div>
              )}
              <div className="flex items-center justify-between font-[var(--font-mono)] uppercase tracking-widest">
                <span>
                  <FormattedMessage id="login.privacy.label" />
                </span>
                <span className="text-white/55">
                  <FormattedMessage id="login.privacy.value" />
                </span>
              </div>
            </div>
          </motion.div>
        </section>
      </main>

      {/* ═══════════════════════════════════════════════════════════
          BOTTOM TICKER — Bloomberg-style
          ═══════════════════════════════════════════════════════════ */}
      <footer className="relative z-10 overflow-hidden border-t border-white/[0.08] bg-black/40 backdrop-blur-md">
        <div className="flex items-center">
          {/* LIVE badge fijo */}
          <div className="flex shrink-0 items-center gap-2 border-r border-white/[0.08] px-5 py-3 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.3em] text-emerald-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            <FormattedMessage id="login.ticker.live" />
          </div>

          {/* Scrolling metrics */}
          <div className="relative flex-1 overflow-hidden">
            <div className="ticker-scroll flex w-max">
              {/* Dos copias para loop */}
              {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0 items-center">
                  {tickerItems.map((item, i) => (
                    <div
                      key={`${copy}-${i}`}
                      className="flex shrink-0 items-center gap-3 px-6 py-3 font-[var(--font-mono)] text-[11px]"
                    >
                      <span className="uppercase tracking-widest text-white/55">
                        {item.label}
                      </span>
                      <span className="tabular-nums text-white/85">{item.value}</span>
                      <span className="text-white/15">│</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ───────────────────────── Subcomponents ─────────────────────────

function StethoscopeMark() {
  return (
    <div className="relative flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] backdrop-blur-sm">
      <span
        aria-hidden
        className="pulse-ring absolute inset-0 rounded-xl border border-[#60a5fa]/40"
      />
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
        <path
          d="M4.8 4.6v5.4a5.4 5.4 0 0 0 10.8 0V4.6M4.8 4.6H3M4.8 4.6h1.8M15.6 4.6h1.8M15.6 4.6h-1.8M10.2 15.4v2.7a3.6 3.6 0 0 0 7.2 0v-1.4"
          stroke="#60a5fa"
          strokeLinecap="round"
        />
        <circle cx="19.2" cy="14.2" r="2.3" stroke="#60a5fa" />
      </svg>
    </div>
  );
}

interface FieldUnderlineProps {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  focused: boolean;
  filled: boolean;
  action?: React.ReactNode;
}
function FieldUnderline({
  icon,
  label,
  children,
  focused,
  filled,
  action,
}: FieldUnderlineProps) {
  return (
    <div className="relative">
      <div className="flex items-center justify-between">
        <label
          className={`flex items-center gap-2 font-[var(--font-mono)] text-[11px] uppercase tracking-[0.24em] transition-colors ${
            focused ? "text-[#60a5fa]" : "text-white/70"
          }`}
        >
          <span className={focused ? "text-[#60a5fa]" : "text-white/70"}>
            {icon}
          </span>
          {label}
        </label>
        {action}
      </div>
      <div className="mt-2">{children}</div>
      {/* Underline con glow cuando está activo */}
      <div className="relative h-px overflow-hidden bg-white/20">
        <div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r from-[#60a5fa] via-[#2563eb] to-[#D4A843] transition-all duration-500 ${
            focused ? "w-full opacity-100" : filled ? "w-full opacity-50" : "w-0 opacity-0"
          }`}
        />
      </div>
    </div>
  );
}
